/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";

const AcmDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const acmAlarms = parsedAttributes.acmAlarms;
  const acmAlarmsMask = parsedAttributes.acmAlarmsMask;
  const acmChassisType = parsedAttributes.acmChassisType || parsedAttributes.chassisType;
  
  // Map chassis type to name
  let chassisTypeName = 'Unknown';
  if (acmChassisType) {
    switch (String(acmChassisType)) {
      case '1':
        chassisTypeName = 'HEU (Head End Unit)';
        break;
      case '2':
        chassisTypeName = 'OIU (Optical Interface Unit)';
        break;
      case '3':
        chassisTypeName = 'IHU (Indoor Head Unit)';
        break;
      default:
        chassisTypeName = `Unknown (${acmChassisType})`;
    }
  }
  
  const hasAlarms = acmAlarms !== undefined || acmAlarmsMask !== undefined;

  if (!hasAlarms) return null;

  const alarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "Over Temperature" },
    { bit: 1, name: "HW Failure" },
    { bit: 2, name: "Adjustment Fault" },
    { bit: 3, name: "Inconsistent Version" },
    { bit: 4, name: "Temperature (PSM A)" },
    { bit: 5, name: "Temperature (PSM B)" },
    { bit: 7, name: "Output Under Voltage (PSM A)" },
    { bit: 8, name: "Output Under Voltage (PSM B)" },
    { bit: 10, name: "Ext1 Clock Failed" },
    { bit: 11, name: "Ext2 Clock Failed" },
    { bit: 12, name: "Master Pilot" },
    { bit: 13, name: "Input Voltage (PSM A)" },
    { bit: 14, name: "Input Voltage (PSM B)" },
    { bit: 15, name: "PSM Failure" },
    { bit: 16, name: "FAM Failure" },
  ];

  const isDisconnected = statusLabel === "Disconnected";

  // If alarmsOnly mode, only render alarm sections
  if (alarmsOnly) {
    if (!hasAlarms) return null;
    
    return (
      <>
        {hasAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={acmAlarms}
                alarmsMask={acmAlarmsMask}
                alarmDefinitions={alarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  // Normal mode: show chassis info and alarms
  if (!acmChassisType && !hasAlarms) return null;

  return (
    <>
      {acmChassisType && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Chassis Information</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <DetailRow label="Chassis Type" value={chassisTypeName} />
          </CardContent>
        </Card>
      )}

      {/* Alarms Section */}
      {hasAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={acmAlarms}
              alarmsMask={acmAlarmsMask}
              alarmDefinitions={alarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default AcmDeviceDetails;
